// ---------------------------------------------------------
// Imports
// ---------------------------------------------------------
import { FunctionComponent, useState } from "react";
import { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import styles from "./LeadDistributorLogic.module.css";
import noData from "../../Assets/animations/no-data.json";
import { themeColors } from "../../Components/theme";
import Dropdown from "./Dropdown";
import { contactResources, editLeadDistributionNormal, editLeadDistributionRoundrobin, fetchConstants, fetchLeadDistributions, fetchpropertySubType } from "../../Services/contacts";
import { showSnackbarAction } from "../../Redux/actions";
import { useLocation } from "react-router-dom";
import Firebase from "firebase/app";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";

// ---------------------------------------------------------
// Component Props & Router Location Types
// ---------------------------------------------------------
interface LocationState {
  data: any; // Type this according to your actual data structure
  index: number;
}

type props = {
  history?: any;
  user?: any;
  organizationUsers?: any;
  theme?: any;
  conatctHeader?: any;
};

// ---------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------
const LeadDistributorLogic: FunctionComponent<props> = ({
  history,
  user,
  organizationUsers,
  theme,
  conatctHeader,
}) => {
  const dispatcher = useDispatch();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  // Access route data (Normal or Roundrobin logic received from previous page)
  const location = useLocation<LocationState>();

  // ---------------------------------------------------------
  // UI State
  // ---------------------------------------------------------
  const [load, setLoad] = useState(false);
  const [maxHeight, setMaxHeight] = useState(window.innerHeight * 0.8);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // To show ONLY Normal OR ONLY Roundrobin card
  const [typeDistributorLogic, setTypeDistributorLogic] = useState(location.state.data.distribution_type);

  // ---------------------------------------------------------
  // Dropdown + Editing States
  // ---------------------------------------------------------
  const [filterData, setFilterData] = useState<any[]>([]);
  const [normalLocationSelected, setNormalLocationSelected] = useState<any>([]);
  const [roundrobinlocationSelected, setRoundrobinlocationSelected] = useState<any>([]);
  const [roundrobinLeadDistId, setRoundrobinLeadDistId] = useState<string>("");
  const [roundrobinIndex, setRoundrobinIndex] = useState<number>(0);
  const [normalLeadDistId, setNormalLeadDistId] = useState<string>("");
  const [normalIndex, setNormalIndex] = useState<number>(0);
  const [roundrobinSourceSelected, setRoundrobinSourceSelected] = useState<any>();
  const [normalSourceSelected, setNormalSourceSelected] = useState<any>();
  const [sourceDuplicateSelected, setDuplicateSourceSelected] = useState<boolean>(false);
  const [roundrobinProjectSelected, setRoundrobinProjectSelected] = useState<any>([]);
  const [normalprojectSelected, setNormalProjectSelected] = useState<any>([]);
  const [roundrobinBudgetSelected, setRoundrobinBudgetSelected] = useState<any>([]);
  const [normalBudgetSelected, setNormalBudgetSelected] = useState<any>([]);
  const [roundrobinPropertyTypeSelected, setRoundrobinPropertyTypeSelected] = useState<any>([]);
  const [normalPropertyTypeSelected, setNormalPropertyTypeSelected] = useState<any>([]);
  const [leadManagerSelected, setLeadManagerSelected] = useState<any>([]);
  const [associateRoundrobinList, setAssociateRoundrobinList] = useState<any[]>([]);
  const [roundrobinAssociateSelected, setRoundrobinAssociateSelected] = useState<any>([]);
  const [normalAssociateSelected, setNormalAssociateSelected] = useState<any>([]);

  // Lists for dropdown options
  const [associateList, setAssociateList] = useState<any[]>([]);
  const [leadManagerList, setLeadManagerList] = useState<any[]>([]);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [locationsList, setLocationsList] = useState<any[]>([]);
  const [budgetsList, setBudgetsList] = useState<any[]>([]);
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);
  const [leadSourceList, setLeadSourceList] = useState<any[]>([]);
  const [comTypes, setComTypes] = useState<any[]>([]);
  const [resTypes, setResTypes] = useState<any[]>([]);

  // UID mapping of users
  const [uid, setUids] = useState<any>({});

  // Use optional chaining to ensure `state` exists
  const { data, index } = location.state || {};

  const handleResize = () => {
    // Update maxHeight when the window is resized
    setMaxHeight(window.innerHeight * 0.8); // Adjust the multiplier as needed
  };

  // ---------------------------------------------------------
  // WINDOW RESIZER: allows form to scroll properly
  // ---------------------------------------------------------
  useEffect(() => {
    // Add event listener for window resize
    window.addEventListener("resize", handleResize);
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  // ---------------------------------------------------------
  // Fetch Lead Distribution List (existing logic)
  // ---------------------------------------------------------
  useEffect(() => {
    if (user.organization_id) {
      const unsub = fetchLeadDistributions(user.organization_id, (data) =>
        setFilterData(data)
      );
      return () => {
        unsub();
      };
    }
  }, [user]);

  // ---------------------------------------------------------
  // Build Lead Manager Dropdown Options
  // ---------------------------------------------------------
  useEffect(() => {
    let leadManagerData: any = [];
    organizationUsers?.forEach((item: any) => {
      const fullNameEmail = `${item.user_first_name} ${item.user_last_name} - ${item.user_email}`;
      if (item.profile === "Lead Manager" || item.profile === "Team Lead") {
        leadManagerData.push(fullNameEmail);
      }
    });
    setLeadManagerList(leadManagerData); // set data for Lead Manager
  }, [organizationUsers]);

  // ---------------------------------------------------------
  // When Lead Manager changes → Build Associate List
  // ---------------------------------------------------------
  useEffect(() => {
    if (leadManagerSelected.length > 0) {
      let associateData: any = [];
      leadManagerSelected.forEach((item: any) => {
        if (item?.value !== "Select") {
          let email: any;
          if (item?.value.includes("-")) {
            let l = item?.value?.split("-");
            if (l && l.length > 1) {
              email = l[1].trim();
            }
          } else {
            email = item?.value.trim();
          }
          organizationUsers?.forEach((orgUser: any) => {
            const fullNameEmail = `${orgUser.user_first_name} ${orgUser.user_last_name} - ${orgUser.user_email}`;
            if (orgUser.reporting_to?.toLowerCase() === email.toLowerCase()) {
              associateData.push(fullNameEmail);
            }
          });
        }
      });
      setAssociateRoundrobinList(associateData);
    }
  }, [leadManagerSelected, organizationUsers]);

  // ---------------------------------------------------------
  // Create Associate List + UID Mapping
  // ---------------------------------------------------------
  useEffect(() => {
    let data: any = [];
    let uids: { [key: string]: string } = {};
    organizationUsers?.forEach((item: any, index: number) => {
      data.push(
        `${item.user_first_name} ${item.user_last_name} -${item.user_email}`
      );
      uids[item.user_email] = item.uid;
    });

    setUids(uids);
    setAssociateList(data);
  }, [organizationUsers]);

  // ---------------------------------------------------------
  // DUPLICATE CHECK: Roundrobin (Source + Project)
  // ---------------------------------------------------------
  useEffect(() => {
    if (
      roundrobinSourceSelected &&
      typeof roundrobinSourceSelected === "object" &&
      roundrobinSourceSelected.value &&
      roundrobinSourceSelected &&
      Array.isArray(roundrobinProjectSelected) &&
      roundrobinProjectSelected.length > 0
    ) {
      // Check if the selected source and project exist in filterData
      const isSourceAndProjectPresent = filterData.some((item) => {
        const isSourceMatch = item.source === roundrobinSourceSelected.value;
        const isProjectMatch = roundrobinProjectSelected.some(
          (project) => item.project.includes(project.value)
        );
        return isSourceMatch && isProjectMatch;
      });

      if (isSourceAndProjectPresent) {
        setDuplicateSourceSelected(true);
        dispatcher(
          showSnackbarAction(
            `Lead distribution logic is already created for ${roundrobinSourceSelected.value} and selected projects`,
            "error"
          )
        );
      } else {
        setDuplicateSourceSelected(false);
        console.log(
          "Selected value does not exist in filterData for both source and project."
        );
      }
    } else if (
      roundrobinSourceSelected &&
      typeof roundrobinSourceSelected === "object" &&
      roundrobinSourceSelected.value
    ) {
      const isSourcePresent = filterData.some(
        (item) => item.source === roundrobinSourceSelected.value
      );
      if (isSourcePresent) {
        setDuplicateSourceSelected(true);
        dispatcher(
          showSnackbarAction(
            `Lead distribution logic is already created on ${roundrobinSourceSelected.value}`,
            "error"
          )
        );
      } else {
        setDuplicateSourceSelected(false);
        console.log("Selected value does not exist in filterData");
      }
    }
  }, [roundrobinSourceSelected, roundrobinProjectSelected]);

  // ---------------------------------------------------------
  // DUPLICATE CHECK: Normal (Source + Project)
  // ---------------------------------------------------------
  useEffect(() => {
    if (
      normalSourceSelected &&
      typeof normalSourceSelected === "object" &&
      normalSourceSelected.value &&
      normalSourceSelected &&
      Array.isArray(normalprojectSelected) &&
      normalprojectSelected.length > 0
    ) {
      // Check if the selected source and project exist in filterData
      const isSourceAndProjectPresent = filterData.some((item) => {
        const isSourceMatch = item.source === normalSourceSelected.value;
        const isProjectMatch = normalprojectSelected.some((project) =>
          item.project.includes(project.value)
        );
        return isSourceMatch && isProjectMatch;
      });

      if (isSourceAndProjectPresent) {
        setDuplicateSourceSelected(true);
        dispatcher(
          showSnackbarAction(
            `Lead distribution logic is already created for ${normalSourceSelected.value} and selected projects`,
            "error"
          )
        );
      } else {
        setDuplicateSourceSelected(false);
        console.log(
          "Selected value does not exist in filterData for both source and project."
        );
      }
    } else if (
      normalSourceSelected &&
      typeof normalSourceSelected === "object" &&
      normalSourceSelected.value
    ) {
      const isSourcePresent = filterData.some(
        (item) => item.source === normalSourceSelected.value
      );
      if (isSourcePresent) {
        setDuplicateSourceSelected(true);
        dispatcher(
          showSnackbarAction(
            `Lead distribution logic is already created on ${normalSourceSelected.value}`,
            "error"
          )
        );
      } else {
        setDuplicateSourceSelected(false);
        console.log("Selected value does not exist in filterData");
      }
    }
  }, [normalSourceSelected, normalprojectSelected]);

  // ---------------------------------------------------------
  // ON PAGE LOAD → Pre-fill form with existing distribution data
  // ---------------------------------------------------------
  useEffect(() => {
    if (data) {
      let project = data.project.map((item: any, index: number) =>
        item === "Team Lead"
          ? {
            value: item,
            label: "Team Leader",
          }
          : { value: item, label: item }
      );
      let location = data.location.map((item: any, index: number) =>
        item === "Team Lead"
          ? {
            value: item,
            label: "Team Leader",
          }
          : { value: item, label: item }
      );
      let budget = data.budget.map((item: any, index: number) =>
        item === "Team Lead"
          ? {
            value: item,
            label: "Team Leader",
          }
          : { value: item, label: item }
      );
      let property_type = data.property_type.map((item: any, index: number) =>
        item === "Team Lead"
          ? {
            value: item,
            label: "Team Leader",
          }
          : { value: item, label: item }
      );
      let users = data.users.map((item: any, index: number) =>
        item === "Team Lead"
          ? {
            value: item,
            label: "Team Leader",
          }
          : { value: item, label: item }
      );
      let leadManager = data?.leadManager_users?.map(
        (item: any, index: number) =>
          item === "Team Lead"
            ? {
              value: item?.user_email,
              label: "Team Leader",
            }
            : { value: item?.user_email, label: item?.user_email }
      );
      if (data.distribution_type === "Normal") {
        setNormalLocationSelected(location);
        setNormalProjectSelected(project);
        setNormalBudgetSelected(budget);
        setNormalPropertyTypeSelected(property_type);
        setNormalSourceSelected({ value: data.source, label: data.source });
        setNormalAssociateSelected(users);
        setNormalLeadDistId(data?.lead_dist_id);
        setNormalIndex(data?.userIndex);
      } else if (data.distribution_type === "Roundrobin") {
        setRoundrobinlocationSelected(location);
        setRoundrobinProjectSelected(project);
        setRoundrobinBudgetSelected(budget);
        setRoundrobinPropertyTypeSelected(property_type);
        setRoundrobinSourceSelected({ value: data.source, label: data.source });
        setLeadManagerSelected(leadManager);
        setRoundrobinLeadDistId(data?.lead_dist_id);
        setRoundrobinIndex(data?.userIndex);
      }
    }
  }, []);

  useEffect(() => {
    if (
      data &&
      data.distribution_type === "Roundrobin" &&
      associateRoundrobinList.length > 0
    ) {
      const labelMap = Object.fromEntries(
        associateRoundrobinList.map((entry) => {
          const [name, email] = entry.split(" - ");
          return [email, entry];
        })
      );
      const updatedUserList = data.users.map((email: any) => ({
        value: labelMap[email] || email,
        label: labelMap[email] || email,
      }));
      setRoundrobinAssociateSelected(updatedUserList);
    }
  }, [associateRoundrobinList, data, leadManagerList]);

  useEffect(() => {
    const unsub = contactResources(
      user.organization_id,
      (data) => setLocationsList(data),
      (data) => setBudgetsList(data),
      (data) => setProjectsList(data)
    );
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setPropertyTypeList(data)
    );
    const unsubProperty = fetchpropertySubType(
      user.organization_id,
      (data) => setComTypes(data),
      (data) => setResTypes(data),
      (data) => setLeadSourceList(data)
    );
    return () => {
      unsub();
      unsubConst();
      unsubProperty();
    };
    // eslint-disable-next-line
  }, []);

  const toggleAccordion = (id: string) => {
    if (id === expandedId) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  // ---------------------------------------------------------
  // Show correct form expanded (Normal / Roundrobin)
  // ---------------------------------------------------------
  useEffect(() => {
    if (data && data?.distribution_type === "Normal") {
      setExpandedId("1");
    } else if (data && data?.distribution_type === "Roundrobin") {
      setExpandedId("2");
    } else {
      setExpandedId(null);
    }
  }, [data, index]);

  // ---------------------------------------------------------
  // SAVE HANDLERS (Normal + Roundrobin)
  // ---------------------------------------------------------
  // FULL COMMENTED VERSIONS CAN BE ADDED — ask if needed
  // ---------------------------------------------------------
  const onEditNormal = () => {
    let source: any = [];
    let project: any = [];
    let location: any = [];
    let budget: any = [];
    let propertyType: any = [];
    let usersList: any = [];
    let usersQueue: any = [];
    normalprojectSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        project.push(item.value);
      }
    });
    if (
      normalSourceSelected &&
      typeof normalSourceSelected === "object" &&
      normalSourceSelected.value !== "Select"
    ) {
      source.push(normalSourceSelected.value);
    }
    normalLocationSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        location.push(item.value);
      }
    });
    normalBudgetSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        budget.push(item.value);
      }
    });
    normalPropertyTypeSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        propertyType.push(item.value);
      }
    });
    if (Array.isArray(normalAssociateSelected)) {
      normalAssociateSelected.forEach((item: any) => {
        if (item.value !== "Select") {
          let l = item.value?.split("-");
          let email = l && l.length > 1 ? l[1].trim() : item.value; // Extract email or use value directly if no "-"

          if (uid[email.trim()] !== undefined) {
            usersList.push({
              uid: uid[email.trim()],
              user_email: email.trim(),
            });
            usersQueue.push(email);
          }
        }
      });
    } else {
      if (normalAssociateSelected?.value !== "Select") {
        let l = normalAssociateSelected.value?.split("-");
        let email = l && l.length > 1 ? l[1].trim() : normalAssociateSelected.value; // Extract email or use value directly if no "-"
        if (uid[email.trim()] !== undefined) {
          usersList.push({
            uid: uid[email.trim()],
            user_email: email.trim(),
          });
          usersQueue.push(email);
        }
      }
    }
    if (usersList.length === 0) {
      dispatcher(showSnackbarAction("Please select an Associate", "error"));
    } else if (source.length === 0) {
      dispatcher(showSnackbarAction("Please select a Lead Source", "error"));
    } else {
      setLoad(true);
      const normalizeUsersData = (users: any) => {
        return users.map((user: any) => {
          const userEmail = typeof user === "string" ? user : user.user_email;
          return {
            user_email: userEmail,
            uid: uid[userEmail] || "", // Look up UID from the mapping
          };
        });
      };
      if (index !== undefined) {
        filterData[index] = {
          source: source[0],
          project: [].concat(...project),
          location: [].concat(...location),
          budget: [].concat(...budget),
          property_type: [].concat(...propertyType),
          users: normalizeUsersData(usersList),
          usersQueue: [].concat(...usersQueue),
          distribution_type: "Normal",
          created_at: Firebase.firestore.Timestamp.now(),
          modify_at: Firebase.firestore.Timestamp.now(),
          userIndex: normalIndex,
          lead_dist_id: normalLeadDistId,
        };
        editLeadDistributionNormal(
          uid,
          user.organization_id,
          filterData,
          dispatcher,
          (data) => setLoad(data),
          history
        );
      }
    }
  };

  const onEditRoundrobin = () => {
    let source: any = [];
    let project: any = [];
    let location: any = [];
    let budget: any = [];
    let propertyType: any = [];
    let usersList: any = [];
    let LeadManagerList: any = [];
    let usersQueue: any = [];

    roundrobinProjectSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        project.push(item.value);
      }
    });
    if (
      roundrobinSourceSelected &&
      typeof roundrobinSourceSelected === "object" &&
      roundrobinSourceSelected.value !== "Select"
    ) {
      source.push(roundrobinSourceSelected.value);
    }
    roundrobinlocationSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        location.push(item.value);
      }
    });
    roundrobinBudgetSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        budget.push(item.value);
      }
    });
    roundrobinPropertyTypeSelected.forEach((item: any) => {
      if (item.value !== "Select") {
        propertyType.push(item.value);
      }
    });

    if (Array.isArray(leadManagerSelected)) {
      leadManagerSelected.forEach((item: any) => {
        if (item.value !== "Select") {
          let l = item.value?.split("-"); // Split based on "-"
          let email = l && l.length > 1 ? l[1].trim() : item.value; // Get the email or use the value directly if no dash exists

          if (uid[email.trim()] !== undefined) {
            LeadManagerList.push({
              uid: uid[email.trim()],
              user_email: email.trim(),
            });
          }
        }
      });
    } else {
      if (leadManagerSelected?.value !== "Select") {
        let l = leadManagerSelected.value?.split("-"); // Split based on "-"
        let email = l && l.length > 1 ? l[1].trim() : leadManagerSelected.value; // Get the email or use the value directly if no dash exists

        if (uid[email.trim()] !== undefined) {
          LeadManagerList.push({
            uid: uid[email.trim()],
            user_email: email.trim(),
          });
        }
      }
    }

    if (Array.isArray(roundrobinAssociateSelected)) {
      roundrobinAssociateSelected.forEach((item: any) => {
        if (item.value !== "Select") {
          let l = item.value?.split("-");
          let email = l && l.length > 1 ? l[1].trim() : item.value; // Extract email or use value directly if no "-"

          if (uid[email.trim()] !== undefined) {
            usersList.push({
              uid: uid[email.trim()],
              user_email: email.trim(),
            });
            usersQueue.push(email);
          }
        }
      });
    } else {
      if (roundrobinAssociateSelected?.value !== "Select") {
        let l = roundrobinAssociateSelected.value?.split("-");
        let email = l && l.length > 1 ? l[1].trim() : roundrobinAssociateSelected.value; // Extract email or use value directly if no "-"

        if (uid[email.trim()] !== undefined) {
          usersList.push({
            uid: uid[email.trim()],
            user_email: email.trim(),
          });
          usersQueue.push(email);
        }
      }
    }
    if (LeadManagerList.length === 0) {
      dispatcher(showSnackbarAction("Please select a Lead Manager", "error"));
    } else if (usersList.length === 0) {
      dispatcher(showSnackbarAction("Please select an Associate", "error"));
    } else if (source.length === 0) {
      dispatcher(showSnackbarAction("Please select a Lead Source", "error"));
    } else {
      setLoad(true);
      const normalizeUsersData = (users: any) => {
        return users.map((user: any) => {
          const userEmail = typeof user === "string" ? user : user.user_email;
          return {
            user_email: userEmail,
            uid: uid[userEmail] || "", // Look up UID from the mapping
          };
        });
      };
      const normalizeLeadManagerData = (leadManagerUsers: any) => {
        return leadManagerUsers.map((user: any) => {
          const userEmail = typeof user === "string" ? user : user.user_email;
          return {
            user_email: userEmail,
            uid: uid[userEmail] || "", // Look up UID from the mapping
          };
        });
      };

      if (index !== undefined) {
        filterData[index] = {
          source: source[0],
          project: [].concat(...project),
          location: [].concat(...location),
          budget: [].concat(...budget),
          property_type: [].concat(...propertyType),
          users: normalizeUsersData(usersList),
          leadManager_users: normalizeLeadManagerData(LeadManagerList),
          usersQueue: [].concat(...usersQueue),
          distribution_type: "Roundrobin",
          created_at: Firebase.firestore.Timestamp.now(),
          modify_at: Firebase.firestore.Timestamp.now(),
          userIndex: roundrobinIndex,
          lead_dist_id: roundrobinLeadDistId,
        };
        editLeadDistributionRoundrobin(
          uid,
          user.organization_id,
          filterData,
          dispatcher,
          (data) => setLoad(data),
          history
        );
      }
    }
  };

  // ---------------------------------------------------------
  // UI Theme Styles
  // ---------------------------------------------------------
  const containerStyles = {
    backgroundColor: theme
      ? themeColors.backgroundColor
      : themeColors.backgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  const cardContainerStyles = {
    backgroundColor: theme
      ? themeColors.cardBackgroundColor
      : themeColors.cardBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  // ---------------------------------------------------------
  // RENDER UI
  // ---------------------------------------------------------
  return (
    <div className={styles.parent} style={{ display: "block", marginTop: isMobile ? "6%" : "3%", ...containerStyles }}>
      <div className={`container-fluid ${isMobile ? "p-2" : "p-4"} d-flex flex-column gap-4`}>
        {isMobile && (
          <div className="d-flex justify-content-between">
            <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)", paddingLeft: "5px", paddingTop: "5px", margin: 0 }}>Edit Lead Distributor Logic Section</h5>
          </div>
        )}
        <div
          className="d-flex flex-column gap-4"
          style={{
            ...containerStyles,
            maxHeight: `${maxHeight}px`,
            overflowX: "hidden",
            overflowY: "scroll",
            paddingBottom: "5%",
          }}
        >
          {(typeDistributorLogic === "Normal") &&
            <div className={`card ${isMobile ? "p-2" : "p-4"} shadow rounded`} style={cardContainerStyles}>
              <div className={styles.headerContainer}>
                <h6
                  className={styles.heading}
                >{isMobile ? 'Normal Assignment' : 'Lead Distribution Logic : Normal Assignment'}</h6>
                <button
                  onClick={() => toggleAccordion("1")}
                >
                  {expandedId === "1" ? (
                    <i className="fas fa-chevron-up" style={{ fontSize: "12px" }} />
                  ) : (
                    <i className="fas fa-chevron-down" style={{ fontSize: "12px" }} />
                  )}
                </button>
              </div>
              {expandedId === "1" && (
                <>
                  <form
                    className={`card shadow rounded mt-2${!isMobile ? ` ${styles.forcePadding}` : ""}`}
                    style={{
                      ...cardContainerStyles,
                      padding: isMobile ? '16px' : '32px',
                      boxSizing: 'border-box',
                      minHeight: isMobile ? 'auto' : undefined,
                    }}
                  >
                    <Grid container spacing={isMobile ? 2 : 3}>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "lead_source"
                            ).value
                          }
                          option={leadSourceList}
                          selectedValue={normalSourceSelected}
                          setSelectedValue={(value) => {
                            setNormalSourceSelected(value);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "project"
                            ).value
                          }
                          isMulti
                          option={projectsList}
                          selectedValue={normalprojectSelected}
                          setSelectedValue={(value) => {
                            setNormalProjectSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "location"
                            ).value
                          }
                          isMulti
                          option={locationsList}
                          selectedValue={normalLocationSelected}
                          setSelectedValue={(value) => {
                            setNormalLocationSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find((obj: any) => obj.label === "budget")
                              .value
                          }
                          isMulti
                          option={budgetsList}
                          selectedValue={normalBudgetSelected}
                          setSelectedValue={(value) => {
                            setNormalBudgetSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "property_type"
                            ).value
                          }
                          isMulti
                          option={propertyTypeList}
                          selectedValue={normalPropertyTypeSelected}
                          setSelectedValue={(value) => {
                            setNormalPropertyTypeSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title="Associate"
                          option={associateList}
                          selectedValue={normalAssociateSelected}
                          setSelectedValue={(value) => {
                            setNormalAssociateSelected(value);
                          }}
                          required
                        />
                      </Grid>
                      {/* Remove submit button from inside form for mobile */}
                      <Grid item xs={12} style={{ marginTop: 12 }}>
                        <StyledSubmitButton
                          onClick={onEditNormal}
                          themeMode={themeMUI.palette.mode}
                        >
                          {load ? (
                            <span style={{ display: "flex", justifyContent: "center" }}>
                              <Circles color="#fff" height={20} width={20} />
                            </span>
                          ) : (
                            <>Apply</>
                          )}
                        </StyledSubmitButton>
                      </Grid>
                    </Grid>
                  </form>
                </>
              )}
            </div>
          }
          {(typeDistributorLogic === "Roundrobin") &&
            <div className={`card ${isMobile ? "p-2" : "p-4"} shadow rounded`} style={cardContainerStyles}>
              <div className={styles.headerContainer}>
                <h6
                  className={styles.heading}
                >{isMobile ? 'Roundrobin Assignment' : 'Lead Distribution Logic : Roundrobin Assignment'}</h6>
                <button
                  onClick={() => toggleAccordion("2")}
                >
                  {expandedId === "2" ? (
                    <i className="fas fa-chevron-up" style={{ fontSize: "12px" }} />
                  ) : (
                    <i className="fas fa-chevron-down" style={{ fontSize: "12px" }} />
                  )}
                </button>
              </div>
              {expandedId === "2" && (
                <>
                  <form
                    className={`card shadow rounded mt-2${!isMobile ? ` ${styles.forcePadding}` : ""}`}
                    style={{
                      ...cardContainerStyles,
                      padding: isMobile ? '16px' : '32px',
                      boxSizing: 'border-box',
                      minHeight: isMobile ? 'auto' : undefined,
                    }}
                  >
                    <Grid container spacing={isMobile ? 2 : 3}>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "lead_source"
                            ).value
                          }
                          option={leadSourceList}
                          selectedValue={roundrobinSourceSelected}
                          setSelectedValue={(value) => {
                            setRoundrobinSourceSelected(value);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "project"
                            ).value
                          }
                          isMulti
                          option={projectsList}
                          selectedValue={roundrobinProjectSelected}
                          setSelectedValue={(value) => {
                            setRoundrobinProjectSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "location"
                            ).value
                          }
                          isMulti
                          option={locationsList}
                          selectedValue={roundrobinlocationSelected}
                          setSelectedValue={(value) => {
                            setRoundrobinlocationSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find((obj: any) => obj.label === "budget")
                              .value
                          }
                          isMulti
                          option={budgetsList}
                          selectedValue={roundrobinBudgetSelected}
                          setSelectedValue={(value) => {
                            setRoundrobinBudgetSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title={
                            conatctHeader.find(
                              (obj: any) => obj.label === "property_type"
                            ).value
                          }
                          isMulti
                          option={propertyTypeList}
                          selectedValue={roundrobinPropertyTypeSelected}
                          setSelectedValue={(value) => {
                            setRoundrobinPropertyTypeSelected(value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title="Lead Manager"
                          isMulti
                          option={leadManagerList}
                          selectedValue={leadManagerSelected}
                          setSelectedValue={(value) => {
                            setLeadManagerSelected(value);
                            setRoundrobinAssociateSelected([])
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Dropdown
                          title="Associate"
                          isMulti
                          option={associateRoundrobinList}
                          selectedValue={roundrobinAssociateSelected}
                          setSelectedValue={(value) => {
                            setRoundrobinAssociateSelected(value);
                          }}
                          required
                        />
                      </Grid>
                      {/* Remove submit button from inside form for mobile */}
                      <Grid item xs={12} style={{ marginTop: 12 }}>
                        <StyledSubmitButton
                          onClick={onEditRoundrobin}
                          themeMode={themeMUI.palette.mode}
                        >
                          {load ? (
                            <span style={{ display: "flex", justifyContent: "center" }}>
                              <Circles color="#fff" height={20} width={20} />
                            </span>
                          ) : (
                            <>Save</>
                          )}
                        </StyledSubmitButton>
                      </Grid>
                    </Grid>
                  </form>
                </>
              )}
            </div>
          }
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------
// Redux: Map Global State To Props
// ---------------------------------------------------------
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode,
    conatctHeader: state.contactHeaderList.data,
  };
};

export default connect(mapStateToProps)(LeadDistributorLogic);
